import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import RevealProvider from '../components/RevealProvider';
import {
  INDEX_COUNTRIES,
  DIM_KEYS,
  DIM_LABELS,
  DEFAULT_WEIGHTS,
  TOP10,
  DIMENSIONS,
  LIMITATIONS,
  CHANGELOG,
} from '../data/indexData';
import './IndexPage.css';

function scoreBucket(score) {
  if (score >= 80) return 'nc-score-high';
  if (score >= 65) return 'nc-score-mid';
  return 'nc-score-low';
}

function calcOverall(c, w) {
  const total = DIM_KEYS.reduce((s, k) => s + (w[k] || 0), 0) || 1;
  const weighted = DIM_KEYS.reduce((s, k) => s + c[k] * (w[k] || 0), 0);
  return Math.round((weighted / total) * 10) / 10;
}

export default function IndexPage() {
  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });

  const ranked = useMemo(() => {
    return INDEX_COUNTRIES
      .map((c) => ({ ...c, overall: calcOverall(c, weights) }))
      .sort((a, b) => b.overall - a.overall);
  }, [weights]);

  const handleSlider = (dim, value) => {
    setWeights((prev) => ({ ...prev, [dim]: value }));
  };

  const resetWeights = () => setWeights({ ...DEFAULT_WEIGHTS });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: 'The Nomad Compass Index — Q1 2026',
    description:
      'Quarterly ranking of 41 digital nomad visa programmes, scored across 8 dimensions: accessibility, processing speed, cost, tax, family viability, permanence, stability, and quality of life.',
    url: 'https://nomadcompasshub.com/nomad-index',
    creator: { '@type': 'Organization', name: 'Nomad Compass' },
    datePublished: '2026-01-15',
    temporalCoverage: '2026-Q1',
    keywords: [
      'digital nomad visa',
      'nomad index',
      'visa ranking',
      'remote work',
      'Portugal D8',
      'Spain DNV',
      'UAE Virtual Working',
    ],
    variableMeasured: DIMENSIONS.map((d) => ({
      '@type': 'PropertyValue',
      name: d.name,
      description: d.what,
    })),
  };

  return (
    <RevealProvider>
      <Helmet>
        <title>The Nomad Compass Index — Q1 2026 | 41 Digital Nomad Visas, Ranked</title>
        <meta
          name="description"
          content="The definitive quarterly ranking of the world's digital nomad visa programmes. 41 countries scored across 8 dimensions. Published by Nomad Compass."
        />
        <link rel="canonical" href="https://nomadcompasshub.com/nomad-index" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="The Nomad Compass Index — Q1 2026" />
        <meta
          property="og:description"
          content="41 digital nomad visas, scored across 8 dimensions. Re-weight for your priorities."
        />
        <meta property="og:url" content="https://nomadcompasshub.com/nomad-index" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Nomad Compass Index — Q1 2026" />
        <meta
          name="twitter:description"
          content="41 digital nomad visas, scored across 8 dimensions. Re-weight for your priorities."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <TopBar variant="home" />

      <div className="nc-index" data-testid="nc-index-page">
        {/* MASTHEAD */}
        <section className="nc-masthead" data-testid="index-masthead">
          <div className="nc-issue-line">Issue N° 01 · Q1 2026</div>
          <h1 className="nc-masthead-title">
            The Nomad<br />Compass <em>Index</em>
          </h1>
          <p className="nc-masthead-deck">
            A quarterly ranking of the world&rsquo;s digital nomad visa programmes — 41 countries
            scored across eight dimensions of accessibility, cost, tax, family viability,
            permanence, stability and life.
          </p>
          <div className="nc-masthead-meta">
            <div className="nc-meta-item">
              <span className="nc-meta-label">Countries ranked</span>
              <span className="nc-meta-val">41</span>
            </div>
            <div className="nc-meta-item">
              <span className="nc-meta-label">Dimensions</span>
              <span className="nc-meta-val">8</span>
            </div>
            <div className="nc-meta-item">
              <span className="nc-meta-label">Published</span>
              <span className="nc-meta-val">January 2026</span>
            </div>
            <div className="nc-meta-item">
              <span className="nc-meta-label">Next issue</span>
              <span className="nc-meta-val">Q2 · April 2026</span>
            </div>
          </div>
        </section>

        {/* EDITOR'S NOTE */}
        <section className="nc-editors reveal" data-testid="index-editors">
          <div className="nc-editors-label">— From the editors</div>
          <div className="nc-editors-body">
            <p>
              This is the first issue of what will become a quarterly reckoning of where in the
              world the globally mobile professional can most sensibly choose to live. Forty-one
              countries now offer a digital nomad visa of some form. The difference between the
              best and the worst programme is the difference between a home and a holding pattern.
            </p>
            <p>
              Portugal retains the top overall score this quarter — not because of any single
              dimension, but because of its unusual combination of{' '}
              <em>five-year citizenship eligibility</em> alongside a tax regime that still, as of
              early 2026, offers meaningful advantage. Spain climbs two places following the
              consolidation of its application process. The UAE holds at #3 on the strength of its
              tax treatment and speed. Indonesia, despite the arrival of KITAS B1, remains outside
              the top ten for reasons our methodology makes visible.
            </p>
            <p>
              The Index is imperfect and openly says so. We publish our methodology in full. We
              publish what we have chosen <em>not</em> to measure. We publish the data behind every
              score. You are invited to re-weight the Index to your own priorities — and to argue
              with us when your experience diverges from our data.
            </p>
          </div>
          <div className="nc-editors-sign">Nomad Compass · Published January 2026</div>
        </section>

        {/* TOP 10 */}
        <section className="nc-top10-section" data-testid="index-top10">
          <div className="nc-top10-inner">
            <div className="nc-top10-head reveal">
              <div className="nc-eyebrow">The Headline Ranking</div>
              <h2 className="nc-title">
                Top 10, <em>this quarter.</em>
              </h2>
              <p className="nc-sub">
                Ranked by overall score — the weighted composite of all eight dimensions.
                Movement indicates change since previous Index. Full 41-country matrix below.
              </p>
            </div>

            <div className="nc-t10-list">
              {TOP10.map((c) => (
                <div
                  key={c.rank}
                  className={`nc-t10-row reveal ${c.featured ? 'featured' : ''}`}
                  data-testid={`t10-row-${c.rank}`}
                >
                  <div className="nc-t10-rank">
                    {String(c.rank).padStart(2, '0')}
                  </div>
                  <div className="nc-t10-country">
                    <div className="nc-t10-name">{c.name}</div>
                    <div className="nc-t10-region">{c.region}</div>
                  </div>
                  <div className="nc-t10-score">
                    <div className="nc-t10-score-val">
                      {c.score.toFixed(1)}
                      <em>/100</em>
                    </div>
                    <div className="nc-t10-score-label">
                      {c.featured ? 'Overall Index Score' : 'Overall Score'}
                    </div>
                  </div>
                  <div className="nc-t10-note">{c.note}</div>
                  <div className={`nc-t10-move ${c.move}`}>{c.moveLabel}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MATRIX */}
        <section className="nc-matrix-section" id="matrix" data-testid="index-matrix">
          <div className="nc-matrix-inner">
            <div className="nc-matrix-head">
              <div className="reveal">
                <div className="nc-eyebrow">The Full Matrix</div>
                <h2 className="nc-title">
                  All 41 countries, scored. <em>Re-weight for your priorities.</em>
                </h2>
                <p className="nc-sub">
                  The Top 10 above uses our default weights. Below, drag the sliders to reflect
                  what actually matters to you — the ranking recalculates in real time. This is
                  your Index, not ours.
                </p>
              </div>

              <div className="nc-matrix-controls reveal" data-testid="matrix-controls">
                <div className="nc-matrix-controls-title">Weight the dimensions</div>
                <div className="nc-matrix-controls-sub">
                  Default weights reflect the Top 10 ranking. Move any slider to prioritise what
                  matters most to you.
                </div>

                {DIM_KEYS.map((dim) => (
                  <div className="nc-slider-row" key={dim}>
                    <div className="nc-slider-info">
                      <label className="nc-slider-label" htmlFor={`slider-${dim}`}>
                        {DIM_LABELS[dim]}
                      </label>
                      <input
                        id={`slider-${dim}`}
                        type="range"
                        className="nc-slider-input"
                        min="0"
                        max="30"
                        value={weights[dim]}
                        onChange={(e) => handleSlider(dim, parseInt(e.target.value, 10))}
                        data-testid={`slider-${dim}`}
                        aria-label={`${DIM_LABELS[dim]} weight`}
                      />
                    </div>
                    <div className="nc-slider-val" data-testid={`slider-val-${dim}`}>
                      {weights[dim]}
                    </div>
                  </div>
                ))}

                <div className="nc-matrix-reset">
                  <button
                    type="button"
                    onClick={resetWeights}
                    data-testid="reset-weights"
                  >
                    Reset to default
                  </button>
                  <span className="nc-matrix-reset-hint">Weights normalise to 100</span>
                </div>
              </div>
            </div>

            <div className="nc-matrix-wrap reveal">
              <table className="nc-matrix" data-testid="matrix-table">
                <thead>
                  <tr>
                    <th className="rank-col num">#</th>
                    <th className="country-col">Country</th>
                    <th className="overall-col num">Overall</th>
                    <th className="num">Access</th>
                    <th className="num">Speed</th>
                    <th className="num">Cost</th>
                    <th className="num">Tax</th>
                    <th className="num">Family</th>
                    <th className="num">Permanence</th>
                    <th className="num">Stability</th>
                    <th className="num">Life</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((c, i) => (
                    <tr
                      key={c.name}
                      className={i < 10 ? 'highlight' : ''}
                      data-testid={`matrix-row-${i + 1}`}
                    >
                      <td className="nc-m-rank">{String(i + 1).padStart(2, '0')}</td>
                      <td>
                        <div className="nc-m-country">
                          <strong>{c.name}</strong>
                          <span>{c.region}</span>
                        </div>
                      </td>
                      <td className="nc-m-overall-cell">
                        <div className="nc-m-overall">{c.overall.toFixed(1)}</div>
                        <div className="nc-m-overall-bar">
                          <span style={{ width: `${c.overall}%` }}></span>
                        </div>
                      </td>
                      {DIM_KEYS.map((k) => (
                        <td key={k} className="nc-m-score">
                          <span className={`nc-score-cell ${scoreBucket(c[k])}`}>{c[k]}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="nc-matrix-foot">
                <div>
                  Showing all 41 ranked countries. Scores normalised 0–100. Last updated: January 2026.
                </div>
                <div>
                  <a href="#methodology">View methodology →</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* METHODOLOGY */}
        <section className="nc-method-section" id="methodology" data-testid="index-methodology">
          <div className="nc-method-grid">
            <div className="nc-method-left reveal">
              <div className="nc-eyebrow">The Methodology</div>
              <h2 className="nc-title">
                Eight dimensions. <em>Open for scrutiny.</em>
              </h2>
              <p>
                The Index scores each of 41 countries on eight measurable dimensions, drawn from a
                combination of government data, public indices, and Nomad Compass&rsquo;s own casework.
              </p>
              <p>
                Every data source is cited. Every formula is documented. Scores update quarterly.
                The complete methodology paper is published alongside each issue and is available
                below.
              </p>
              <Link to="/#enquiry" className="nc-method-download">
                Request the methodology paper (PDF) →
              </Link>
            </div>

            <div className="nc-dim-list reveal">
              {DIMENSIONS.map((d) => (
                <div className="nc-dim" key={d.n} data-testid={`dim-${d.n}`}>
                  <div className="nc-dim-head">
                    <div className="nc-dim-name">
                      <span className="nc-dim-num">{d.n}</span>
                      {d.name}
                    </div>
                    <div className="nc-dim-weight">
                      Weight · <strong>{d.weight}</strong>
                    </div>
                  </div>
                  <div className="nc-dim-what">{d.what}</div>
                  <div className="nc-dim-source">
                    <strong>Source:</strong> {d.source}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* LIMITATIONS */}
        <section className="nc-limits-section" data-testid="index-limits">
          <div className="nc-limits-wrap reveal">
            <div className="nc-eyebrow center">What this Index does not measure</div>
            <h2 className="nc-title">The honest caveats.</h2>
            <p className="nc-sub">
              A ranking is only as credible as what it admits it cannot measure. These are the
              factors we have deliberately excluded and why.
            </p>

            <div className="nc-limits-list">
              {LIMITATIONS.map((l, i) => (
                <div className="nc-limit-item" key={i}>
                  <strong>{l.title}</strong>
                  <span>{l.body}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CHANGELOG */}
        <section className="nc-changelog" data-testid="index-changelog">
          <div className="nc-changelog-inner">
            <div className="nc-eyebrow center reveal">Since last issue</div>
            <h2 className="nc-title reveal">What moved and why.</h2>
            <p className="nc-sub reveal">
              This is the first issue of the Index, so the changes below reflect recent shifts in
              the underlying programmes in the run-up to publication. Future issues will track
              movement quarter over quarter.
            </p>

            {CHANGELOG.map((c, i) => (
              <div className="nc-change-item reveal" key={i}>
                <div className="nc-change-date">{c.date}</div>
                <div className="nc-change-body">
                  <strong>{c.title}</strong> {c.body}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="nc-cta-block" data-testid="index-cta">
          <div className="nc-eyebrow">The Nomad Compass service</div>
          <h2 className="nc-cta-title">
            Rankings are a start. <em>Applications get filed.</em>
          </h2>
          <p className="nc-cta-sub">
            The Index helps you choose. We help you apply — end to end, one flat fee, one named
            expert. For any of the 41 countries.
          </p>
          <div className="nc-cta-actions">
            <Link to="/#enquiry" className="nc-btn-primary-inv" data-testid="index-cta-apply">
              Start my application
            </Link>
            <Link to="/quiz" className="nc-btn-ghost-inv" data-testid="index-cta-quiz">
              Take the Visa Quiz
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </RevealProvider>
  );
}
